import { yupResolver } from '@hookform/resolvers/yup';
import { LANGUAGE_PAIR_DEFAULT, PAGE_DEFAULT, QUERY_DEFAULT } from 'constants/defaults';
import useOptionsPairLanguagesTM from 'hooks/useOptionsPairLanguagesTM';
import _ from 'lodash';
import { usePathname, useSearchParams } from 'next/navigation';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { Option } from 'types/Option';
import { TScheme } from 'types/TScheme';
import { useDebounce } from 'use-debounce';
import { extractParam } from 'utils/paramsUtil';
import * as yup from 'yup';

export interface FiltersTranslationMemoryForm {
  languagePair: string;
  query: string | undefined;
  page: number;
}

interface IUseSearchParamsTMOut {
  control: Control<FiltersTranslationMemoryForm>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  languagePair: string;
  languagesDescription?: string;
  deferredSearchQuery?: string;
  optionsLanguagesTM: Option[];
  searchQuery?: string;
}

export default function useSearchParamsTM(): IUseSearchParamsTMOut {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [page, setPage] = useState(PAGE_DEFAULT);

  const { optionsLanguagesTM } = useOptionsPairLanguagesTM();

  const pageInitial = extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT);

  const queryInitial = extractParam(searchParams, KeysEnum.Query, QUERY_DEFAULT);

  const isList = pathname.includes('/translation-memory/list');

  const languagePairInitial = extractParam(
    searchParams,
    KeysEnum.LanguagePair,
    LANGUAGE_PAIR_DEFAULT
  ) as string;

  const schema = yup.object().shape({} as TScheme<FiltersTranslationMemoryForm>);
  const {
    control,
    setValue,
    formState: { isDirty }
  } = useForm<FiltersTranslationMemoryForm>({
    resolver: yupResolver(schema)
  });

  const query = useWatch({
    control,
    name: KeysEnum.Query,
    defaultValue: QUERY_DEFAULT
  });

  const [deferredQuery] = useDebounce(query, 1000);

  const languagePair = useWatch({
    control,
    name: KeysEnum.LanguagePair,
    defaultValue: LANGUAGE_PAIR_DEFAULT
  });

  const languagesDescription = optionsLanguagesTM?.find(
    (lang) => lang.value === languagePair
  )?.label;

  useEffect(() => {
    setPage(pageInitial);
    setValue(KeysEnum.Query, queryInitial);

    if (!!languagePairInitial) setValue(KeysEnum.LanguagePair, languagePairInitial);
    else if (!!(optionsLanguagesTM?.at(0)?.value as string))
      setValue(KeysEnum.LanguagePair, optionsLanguagesTM?.at(0)?.value as string);
  }, [languagePairInitial, optionsLanguagesTM, pageInitial, queryInitial, setValue]);

  useEffect(() => {
    if (typeof window !== 'undefined' && isList) {
      const url = new URL(window.location.href);

      if (deferredQuery) url.searchParams.set(KeysEnum.Query, JSON.stringify(deferredQuery));
      else url.searchParams.delete(KeysEnum.Query);

      if (!!languagePair) url.searchParams.set(KeysEnum.LanguagePair, JSON.stringify(languagePair));
      else url.searchParams.delete(KeysEnum.LanguagePair);

      url.searchParams.delete(KeysEnum.Page);
      setPage(
        [queryInitial !== deferredQuery, !_.isEqual(languagePairInitial, languagePair)].some(
          Boolean
        ) && isDirty
          ? PAGE_DEFAULT
          : pageInitial
      );

      window.history.replaceState({}, '', url.toString());
    }
  }, [
    deferredQuery,
    languagePair,
    languagePairInitial,
    pageInitial,
    query,
    isList,
    queryInitial,
    isDirty
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined' && isList) {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname, isList]);

  return {
    control,
    page,
    setPage,
    languagePair,
    deferredSearchQuery: deferredQuery,
    searchQuery: query,
    languagesDescription,
    optionsLanguagesTM
  };
}
